import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { clickable } from '../../../../../utils/a11y';
import DutyHolderSuggestions from './DutyHolderSuggestions';

/**
 * <DutyHolderPanel /> — a building's finance page.
 *
 * Harvey (16 Oct): "Where do I put the finance information for a building? There
 * needs to be a finance page for each building… The bold is the duty holder
 * invoicing detail which is relevant to each building. The c/o is managing agents
 * address and email address."
 *
 * The details themselves live on the client record (Josh's model already holds
 * every field — name, reference, invoice email, billing address). This page shows
 * them against the building, in the same shape as an invoice, and lets you choose
 * which linked client is the duty holder. Editing stays on the client record so
 * there's one source of truth.
 */

const BASE = '/contractor/workspace';
const card = { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' };
const muted = { color: 'var(--cw-text-muted)', marginRight: 7, width: 14, textAlign: 'center' };

/** The invoicing block, laid out exactly as Harvey's example. */
const InvoiceBlock = ({ h, onOpen }) => (
  <div style={{ padding: '16px 18px' }}>
    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--cw-text)', lineHeight: 1.3 }}>{h.name}</div>
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8, fontSize: '0.86rem', color: 'var(--cw-text-2)' }}>
      {h.invoice_email ? (
        <span>
          <i className="fa-solid fa-envelope" style={muted} />
          <a href={`mailto:${h.invoice_email}`} style={{ color: 'var(--cw-blue)' }}>{h.invoice_email}</a>
          {h.invoice_email_is_fallback && <span style={{ fontSize: '0.72rem', color: '#b45309', marginLeft: 6 }}>(general contact — no accounts email set)</span>}
        </span>
      ) : (
        <span style={{ color: '#b45309' }}><i className="fa-solid fa-envelope" style={muted} />No invoice email</span>
      )}
      {h.reference
        ? <span><i className="fa-solid fa-hashtag" style={muted} />{h.reference}</span>
        : <span style={{ color: '#b45309' }}><i className="fa-solid fa-hashtag" style={muted} />No account reference</span>}
    </div>
    <div style={{ marginTop: 6, fontSize: '0.86rem', color: 'var(--cw-text-2)' }}>
      {h.billing_address ? (
        <span>
          <i className="fa-solid fa-location-dot" style={muted} />{h.billing_address}
          {h.billing_address_is_fallback && <span style={{ fontSize: '0.72rem', color: '#b45309', marginLeft: 6 }}>(client's own address — no billing address set)</span>}
        </span>
      ) : (
        <span style={{ color: '#b45309' }}><i className="fa-solid fa-location-dot" style={muted} />No billing / c/o address</span>
      )}
    </div>
    <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
      <button className="cw-btn cw-btn-secondary" style={{ fontSize: '0.8rem' }} onClick={onOpen}>
        <i className="fa-solid fa-arrow-up-right-from-square" /> Open client billing
      </button>
    </div>
  </div>
);

const DutyHolderPanel = ({ propertyId = null, localPropertyId = null, propertyName = '', compact = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ dutyHolder: null, linked: [], ambiguous: false });
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);

  const isLocal = !propertyId && !!localPropertyId;
  const pid = propertyId || localPropertyId;

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const d = await apiFetch(`/api/contractor/properties/${pid}/duty-holder${isLocal ? '?local=1' : ''}`);
      setData({ dutyHolder: d?.dutyHolder || null, linked: d?.linked || [], ambiguous: !!d?.ambiguous });
    } catch { setData({ dutyHolder: null, linked: [], ambiguous: false }); }
    finally { setLoading(false); }
  }, [pid, isLocal]);
  useEffect(() => { load(); }, [load]);

  // Every client, for the "link a duty holder" picker.
  useEffect(() => {
    if (compact) return;
    apiFetch('/api/contractor/clients')
      .then((d) => setClients(Array.isArray(d) ? d : (d?.clients || [])))
      .catch(() => setClients([]));
  }, [compact]);

  const openClient = (clientId) => navigate(`${BASE}/clients/${clientId}?tab=billing`);

  const link = async () => {
    if (!pick) { toast.error('Choose a client'); return; }
    setBusy(true);
    try {
      await apiFetch(`/api/contractor/clients/${pick}/properties`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isLocal ? { local_property_id: pid } : { property_id: pid }),
      });
      // A building's first client is its duty holder; mark it so it stays that way
      // if another client is linked later.
      const d = await apiFetch(`/api/contractor/properties/${pid}/duty-holder${isLocal ? '?local=1' : ''}`);
      const justLinked = (d?.linked || []).find((l) => String(l.client_id) === String(pick));
      if (justLinked && (d?.linked || []).length === 1) {
        await apiFetch(`/api/contractor/clients/${pick}/properties/${justLinked.link_id}/primary`, { method: 'PUT' });
      }
      toast.success('Duty holder linked');
      setPick('');
      load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not link that client')); }
    finally { setBusy(false); }
  };

  const makeDutyHolder = async (l) => {
    setBusy(true);
    try {
      await apiFetch(`/api/contractor/clients/${l.client_id}/properties/${l.link_id}/primary`, { method: 'PUT' });
      toast.success(`${l.name} is now the duty holder for ${propertyName || 'this building'}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not set the duty holder')); }
    finally { setBusy(false); }
  };

  const linkedIds = new Set(data.linked.map((l) => String(l.client_id)));
  const available = clients.filter((c) => !linkedIds.has(String(c.id)));
  const others = data.linked.filter((l) => !data.dutyHolder || l.client_id !== data.dutyHolder.client_id);
  const missing = data.dutyHolder
    ? ['invoice_email', 'reference', 'billing_address'].filter((k) => !data.dutyHolder[k])
    : [];

  // On a job: just who it'll be invoiced to, with a route to fix it on the building.
  if (compact) {
    if (loading) return null;
    return (
      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--cw-text-muted)' }}>Invoiced to</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--cw-text-muted)' }}>— the building's duty holder</span>
        </div>
        {data.dutyHolder
          ? <InvoiceBlock h={data.dutyHolder} onOpen={() => openClient(data.dutyHolder.client_id)} />
          : (
            <div style={{ padding: '13px 16px', fontSize: '0.83rem', color: '#92400e' }}>
              <i className="fa-solid fa-triangle-exclamation" />{' '}
              {data.ambiguous
                ? 'Several clients are linked to this building and none is marked as the duty holder.'
                : 'No duty holder is linked to this building, so invoices will have no email, reference or c/o address.'}
              {' '}Set it on the building's Finance tab.
            </div>
          )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '0.8rem', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '11px 14px', lineHeight: 1.5 }}>
        <i className="fa-solid fa-circle-info" style={{ marginTop: 2, color: 'var(--cw-blue)' }} />
        <span>
          The <strong>duty holder</strong> is who invoices for {propertyName || 'this building'} are addressed to — the
          legal entity, their accounts email and reference, and the c/o managing agent's address. It's pulled onto every
          invoice raised here automatically. Edit the details on the client's Billing tab.
        </span>
      </div>

      {/* The duty holder itself */}
      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ padding: '10px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--cw-text-muted)' }}>
          Duty holder — invoicing details
        </div>
        {loading ? (
          <div style={{ padding: '22px 18px', color: 'var(--cw-text-muted)', fontSize: '0.84rem' }}><i className="fa-solid fa-spinner fa-spin" /> Loading…</div>
        ) : data.dutyHolder ? (
          <>
            <InvoiceBlock h={data.dutyHolder} onOpen={() => openClient(data.dutyHolder.client_id)} />
            {missing.length > 0 && (
              <div style={{ margin: '0 18px 16px', fontSize: '0.79rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px' }}>
                <i className="fa-solid fa-triangle-exclamation" /> Missing {missing.map((k) => ({ invoice_email: 'invoice email', reference: 'account reference', billing_address: 'c/o address' }[k])).join(', ')} —
                {' '}invoices for this building won't be complete until it's added on the client's Billing tab.
              </div>
            )}
          </>
        ) : data.ambiguous ? (
          <div style={{ padding: '16px 18px', fontSize: '0.85rem', color: '#92400e' }}>
            <i className="fa-solid fa-triangle-exclamation" /> {data.linked.length} clients are linked to this building and none is
            marked as the duty holder. Choose one below — invoices won't guess which legal entity to address.
          </div>
        ) : (
          <div style={{ padding: '18px', fontSize: '0.85rem', color: 'var(--cw-text-muted)' }}>
            No duty holder linked yet. Invoices for this building will fall back to the names on Related Companies, with no
            email, reference or c/o address. Link one below.
          </div>
        )}
      </div>

      {/* Other linked clients — any can be promoted */}
      {others.length > 0 && (
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '10px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--cw-text-muted)' }}>
            Also linked to this building
          </div>
          {others.map((l) => (
            <div key={l.link_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderTop: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
              <div {...clickable(() => openClient(l.client_id))} style={{ flex: '1 1 220px', minWidth: 0, cursor: 'pointer' }}>
                <div style={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--cw-blue)' }}>{l.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--cw-text-muted)' }}>{[l.reference && `# ${l.reference}`, l.invoice_email].filter(Boolean).join('  ·  ') || 'No billing details yet'}</div>
              </div>
              <button className="cw-btn cw-btn-secondary" style={{ fontSize: '0.76rem' }} disabled={busy} onClick={() => makeDutyHolder(l)}>
                Make duty holder
              </button>
            </div>
          ))}
        </div>
      )}

      {/* What's already typed on Related Companies, offered as links. */}
      <DutyHolderSuggestions propertyId={propertyId} localPropertyId={localPropertyId} onChanged={load} />

      {/* Link another client */}
      <div style={{ ...card, padding: 14 }}>
        <div className="cw-field-label" style={{ marginBottom: 6 }}>{data.linked.length ? 'Link another client' : 'Link a duty holder'}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="cw-input" style={{ flex: '1 1 260px', padding: '0.5rem 0.65rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem' }}
            value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">{available.length ? 'Choose a client…' : 'Every client is already linked'}</option>
            {available.map((c) => <option key={c.id} value={c.id}>{c.name}{c.reference ? ` (${c.reference})` : ''}</option>)}
          </select>
          <button className="cw-btn cw-btn-primary" disabled={busy || !pick} onClick={link}>
            <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-link'}`} /> Link
          </button>
          <button className="cw-btn cw-btn-secondary" onClick={() => navigate(`${BASE}/clients`)}>
            <i className="fa-solid fa-plus" /> New client
          </button>
        </div>
        <div style={{ fontSize: '0.74rem', color: 'var(--cw-text-muted)', marginTop: 6 }}>
          The client's own Properties tab lists every building it's linked to — the link works both ways.
        </div>
      </div>
    </div>
  );
};

export default DutyHolderPanel;
